import { Component, ElementRef, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, of, Subject } from 'rxjs';
import { delay, takeUntil, tap } from 'rxjs/operators';
import { NotificationService } from '@services/notification.service';
import { RecipeLite } from '@shared/models/recipe/recipe-lite.model';
import { BinariesService } from '@services/binaries/binaries.service';
import { APP_CONFIG } from '@services/config.service';
import { RecipeService } from '@services/recipe/recipe.service';
import { BINARY_TARGET_DOMAIN } from '@packs/utils/pack.utils';

interface InputEditNamePopin {
  recipeOriginal: RecipeLite;
  isSkeletonRecipe: boolean;
  isAbacusRecipe: boolean;
  isEditSoftHyphenAllowed$: Observable<boolean>;
}

@Component({
  selector: 'app-edit-name-popin',
  templateUrl: 'edit-name-popin.component.html',
  styleUrls: ['edit-name-popin.component.scss'],
  standalone: false,
})
export class EditNamePopinComponent implements OnInit, OnDestroy {
  @ViewChild('titleInput') titleInput: ElementRef;
  @Input() isEditSoftHyphenAllowed$: Observable<boolean> = of(false);
  formRecipe = this.formBuilder.group({
    title: '',
    shortTitle: '',
  });
  applianceGroups: string[] = [];
  recipeOriginal: RecipeLite;
  isSkeletonRecipe = false;
  isAbacusRecipe = false;
  isSubmitted = false;
  APP_CONFIG = APP_CONFIG;
  buttonDisabled: Subject<boolean> = new Subject();
  isButtonDisabled = true;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly recipeService: RecipeService,
    private readonly translateService: TranslateService,
    private readonly notificationService: NotificationService,
    private readonly formBuilder: FormBuilder,
    private readonly binariesService: BinariesService,
    public dialogRef: MatDialogRef<EditNamePopinComponent>,
    @Inject(MAT_DIALOG_DATA) public data: InputEditNamePopin,
  ) {
    this.recipeOriginal = data.recipeOriginal;
    this.applianceGroups = data.recipeOriginal.applianceGroups;
    this.isSkeletonRecipe = data.isSkeletonRecipe;
    this.isAbacusRecipe = data.isAbacusRecipe;
    this.formRecipe = this.formBuilder.group({
      title: data.recipeOriginal.title,
      shortTitle: data.recipeOriginal.shortTitle,
    });
    this.isEditSoftHyphenAllowed$ = data.isEditSoftHyphenAllowed$;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.formRecipe.controls.title.setValue(this.formRecipe?.controls.title.value?.replace(/­/g, '(-)') ?? '');
    this.buttonDisabled
      .pipe(
        delay(200),
        tap((isDisabled) => (this.isButtonDisabled = isDisabled)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  cancel(): void {
    this.dialogRef.close();
  }

  submitSelection(): void {
    if (this.formRecipe.invalid || this.isSubmitted) {
      return;
    }
    this.recipeOriginal.title = this.formRecipe?.controls.title.value?.replace(/\(-\)/g, '­') ?? '';
    this.recipeOriginal.shortTitle = this.formRecipe.controls.shortTitle.value ?? '';

    this.isSubmitted = true;
    this.recipeService.updateRecipe(this.recipeOriginal).subscribe({
      next: (recipeUpdated: RecipeLite) => {
        this.isSubmitted = false;
        this.recipeOriginal = recipeUpdated;
        this.notificationService.sendSuccess(this.translateService.instant('RECIPE_UPDATED'), true);
        if (this.recipeOriginal?.domain === BINARY_TARGET_DOMAIN) {
          this.notificationService.sendSuccess(this.translateService.instant('BINARIES.INFO_MSG'), true);
        }
        this.dialogRef.close(this.recipeOriginal);
      },
      error: (err: HttpErrorResponse) => {
        this.errorManager(err);
      },
    });
  }

  errorManager(err: HttpErrorResponse): void {
    let errorText = null;
    if (err.status && err.status === 403) {
      errorText = this.translateService.instant('RECIPE_UPDATE_FORBIDDEN');
      this.notificationService.sendError(errorText);
    } else {
      this.notificationService.sendAPIError(err);
    }
    this.isSubmitted = false;
  }

  bineratorLenghtTestOnFocus(propertyName: string): void {
    // call when lost focus
    if (this.recipeOriginal.lang === 'ar') {
      this.bineratorFieldLengthValid(propertyName);
    }
  }

  bineratorLenghtTest(propertyName: string): void {
    if (this.recipeOriginal.lang !== 'ar') {
      this.bineratorFieldLengthValid(propertyName);
    }
  }

  bineratorFieldLengthValid(propertyName: string): void {
    if (this.isBineratorControlsActivated()) {
      this.binariesService
        .isFieldLengthValid(this.recipeOriginal.market, this.recipeOriginal.lang, propertyName, this.formRecipe.get(propertyName)?.value)
        .subscribe((isValid: any) => {
          if (isValid) {
            this.formRecipe.get(propertyName)?.setErrors(null);
          } else {
            this.formRecipe.get(propertyName)?.setErrors({ binerator: true });
          }
        });
    }
  }

  isBineratorControlsActivated(): boolean {
    // binerator controls should be activated for Cookeo USB/Connect & Cookeo Mini
    let hasApplianceGroupForBineratorControls = false;
    (this.recipeOriginal.applianceGroups || []).forEach((applianceGroup: any) => {
      if (this.APP_CONFIG.binerator.applianceGroupsActivatedForControls.indexOf(applianceGroup) > -1) {
        hasApplianceGroupForBineratorControls = true;
      }
    });
    return (
      this.recipeOriginal.domain === BINARY_TARGET_DOMAIN &&
      hasApplianceGroupForBineratorControls &&
      !this.isSkeletonRecipe &&
      !this.isAbacusRecipe
    );
  }

  addSoftHyphen(): void {
    const inputElem = this.titleInput.nativeElement;
    const startPos = inputElem.selectionStart;
    const endPos = inputElem.selectionEnd;
    const previousValue = inputElem.value;
    let newValue = [previousValue.slice(0, startPos), '­', previousValue.slice(endPos)].join('');

    this.formRecipe.controls.title.setValue(newValue);
    newValue = newValue.replace(/­/g, '(-)');
    inputElem.value = newValue;

    inputElem.selectionStart = inputElem.selectionEnd = startPos + 3;
  }

  onFocus(): void {
    this.buttonDisabled.next(false);
  }

  onBlur(): void {
    this.buttonDisabled.next(true);
  }
}
